import * as React from "react";
import styled, { css } from "styled-components";

import { truncateLines } from "../../common/utils/style";
import ImageComponent, { Image } from "../../common/components/Image";
import Hover from "../../common/components/Hover";

type Company = WtdModels.Partial<
  WtdModels.Company,
  "company_path" | "name" | "short_location" | "mission_statement",
  {
    avatar: WtdModels.HasOne<Image>;
    background: WtdModels.HasOne<Image>;
  }
>;

export interface CompanyCardModels {
  Company: Company;
}

interface Props {
  className?: string;
  company: Company;
  floatsByHover?: boolean;
}

const CompanyCard: React.SFC<Props> = props => {
  const { company, floatsByHover } = props;

  return (
    <Hover>
      {({ isHovered, ref }) => (
        <Base
          href={company.company_path}
          innerRef={ref}
          isHovered={Boolean(floatsByHover) && isHovered}
          className={props.className}
        >
          <CoverImageWrapper>
            {company.background && (
              <CompanyCoverImage image={company.background} size="330x90#" alt={company.name} background />
            )}
          </CoverImageWrapper>
          {company.avatar && <CompanyAvatar image={company.avatar} size="70x70#" alt={company.name} />}
          <CompanyMeta>
            <CompanyName>{company.name}</CompanyName>
            {company.short_location && (
              <CompanyLocation>
                <LocationIcon className="wt-icon wt-icon-location" />
                {company.short_location}
              </CompanyLocation>
            )}
            {company.mission_statement && <TagLine>{company.mission_statement}</TagLine>}
          </CompanyMeta>
        </Base>
      )}
    </Hover>
  );
};

interface StyleProps {
  isHovered: boolean;
}

const Base = styled.a`
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 234px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #ffffff;
  transform: translateY(0);
  transition: transform 200ms ease-out, box-shadow 200ms ease-out;
  &:hover {
    text-decoration: none;
  }
  ${(props: StyleProps) =>
    props.isHovered
      ? css`
          transform: translateY(-5px);
          box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.1);
        `
      : css``};
`;

const CoverImageWrapper = styled.div`
  height: 90px;
  background-color: #bbb;
`;

const CompanyCoverImage = styled(ImageComponent)`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
`;

const CompanyAvatar = styled(ImageComponent)`
  position: absolute;
  top: 55px;
  right: 16px;
  width: 70px;
  height: 70px;
  border: solid 2px #ffffff;
  border-radius: 50%;
  box-sizing: border-box;
`;

const CompanyMeta = styled.div`
  max-width: calc(100% - 87px);
  padding: 14px 16px 0;
  box-sizing: border-box;
`;

const CompanyName = styled.div`
  flex-grow: 0;
  font-weight: 600;
  font-size: 18px;
  line-height: 1.3;
  ${truncateLines(2, 18, 1.3)};
  color: rgba(0, 0, 0, 0.8);
  margin-bottom: 9px;
`;

const CompanyLocation = styled.div`
  display: flex;
  font-weight: 400;
  font-size: 13px;
  text-align: left;
  line-height: 1;
  color: rgba(0, 0, 0, 0.56);
`;

const LocationIcon = styled.i`
  font-size: 14px;
  margin-top: -2.5px;
  margin-right: 4px;
`;

const TagLine = styled.div`
  font-weight: 600;
  font-size: 13px;
  margin-top: 11px;
  line-height: 1.5;
  ${truncateLines(2, 13, 1.5)};
  color: rgba(0, 0, 0, 0.8);
`;

export default CompanyCard;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/CompanyCard.tsx